import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Form, Label, Input, FormFeedback, Alert } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { ticketRequestsAPI, projetsAPI } from '../../services/api';
import AlertModal from '../../components/Common/AlertModal';
import { useNavigate } from 'react-router-dom';

const CreateTicketRequest = () => {
    document.title = "Nouvelle demande de ticket | Ticket Management";

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [projects, setProjects] = useState([]);
    const [etapes, setEtapes] = useState([]);
    const [alertConfig, setAlertConfig] = useState({
        isOpen: false,
        type: 'success',
        message: '',
        title: ''
    });

    const toggleAlert = () => {
        if (alertConfig.isOpen && alertConfig.type === 'success') {
            navigate('/my-ticket-requests');
        }
        setAlertConfig({ ...alertConfig, isOpen: !alertConfig.isOpen });
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            const response = await projetsAPI.getAll();
            setProjects(response.data);
        } catch (error) {
            console.error('Erreur lors du chargement des projets:', error);
            toast.error('Erreur lors du chargement des projets');
        }
    };

    const fetchEtapes = async (projectId) => {
        try {
            const response = await projetsAPI.getEtapes(projectId);
            setEtapes(response.data);
        } catch (error) {
            console.error('Erreur lors du chargement des étapes:', error);
            setEtapes([]);
        }
    };

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            titre: '',
            description: '',
            projet_id: '',
            etape_id: '',
            priorite: 'normale',
        },
        validationSchema: Yup.object({
            titre: Yup.string().required('Le titre est requis'),
            description: Yup.string().required('La description est requise'),
            projet_id: Yup.string().required('Veuillez sélectionner un projet'),
            priorite: Yup.string().required('Veuillez sélectionner une priorité'),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                await ticketRequestsAPI.create(values);
                setAlertConfig({
                    isOpen: true,
                    type: 'success',
                    title: 'Demande envoyée',
                    message: 'Votre demande de ticket a été créée avec succès ! Elle sera examinée par un administrateur.'
                });
            } catch (error) {
                const message = error.response?.data?.message || 'Erreur lors de la création de la demande';
                setAlertConfig({
                    isOpen: true,
                    type: 'error',
                    title: 'Erreur',
                    message: message
                });
            } finally {
                setLoading(false);
            }
        },
    });

    const handleProjectChange = (e) => {
        const projectId = e.target.value;
        validation.setFieldValue('projet_id', projectId);
        validation.setFieldValue('etape_id', '');

        if (projectId) {
            fetchEtapes(projectId);
        } else {
            setEtapes([]);
        }
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Demandes" breadcrumbItem="Nouvelle demande de ticket" />

                    <Row>
                        <Col lg={8}>
                            <Card>
                                <CardBody>
                                    <h4 className="card-title mb-4">Créer une demande de ticket</h4>

                                    <Alert color="info" className="mb-4">
                                        <i className="mdi mdi-information-outline me-2"></i>
                                        Votre demande sera examinée par un administrateur qui créera un ticket si elle est approuvée.
                                    </Alert>

                                    <Form onSubmit={validation.handleSubmit}>
                                        <Row>
                                            <Col md={12}>
                                                <div className="mb-3">
                                                    <Label htmlFor="titre">Titre de la demande</Label>
                                                    <Input
                                                        type="text"
                                                        className="form-control"
                                                        id="titre"
                                                        name="titre"
                                                        placeholder="Ex: Problème d'affichage sur la page d'accueil"
                                                        value={validation.values.titre}
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        invalid={validation.touched.titre && validation.errors.titre}
                                                    />
                                                    {validation.touched.titre && validation.errors.titre && (
                                                        <FormFeedback>{validation.errors.titre}</FormFeedback>
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col md={6}>
                                                <div className="mb-3">
                                                    <Label htmlFor="projet_id">Projet</Label>
                                                    <Input
                                                        type="select"
                                                        className="form-control"
                                                        id="projet_id"
                                                        name="projet_id"
                                                        value={validation.values.projet_id}
                                                        onChange={handleProjectChange}
                                                        onBlur={validation.handleBlur}
                                                        invalid={validation.touched.projet_id && validation.errors.projet_id}
                                                    >
                                                        <option value="">Sélectionnez un projet</option>
                                                        {projects.map((projet) => (
                                                            <option key={projet.id} value={projet.id}>
                                                                {projet.nom}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                    {validation.touched.projet_id && validation.errors.projet_id && (
                                                        <FormFeedback>{validation.errors.projet_id}</FormFeedback>
                                                    )}
                                                </div>
                                            </Col>

                                            <Col md={6}>
                                                <div className="mb-3">
                                                    <Label htmlFor="etape_id">Étape (optionnel)</Label>
                                                    <Input
                                                        type="select"
                                                        className="form-control"
                                                        id="etape_id"
                                                        name="etape_id"
                                                        value={validation.values.etape_id}
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        disabled={!validation.values.projet_id || etapes.length === 0}
                                                    >
                                                        <option value="">Sélectionnez une étape (optionnel)</option>
                                                        {etapes.map((etape) => (
                                                            <option key={etape.id} value={etape.id}>
                                                                {etape.nom}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                </div>
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col md={12}>
                                                <div className="mb-3">
                                                    <Label htmlFor="priorite">Priorité</Label>
                                                    <Input
                                                        type="select"
                                                        className="form-control"
                                                        id="priorite"
                                                        name="priorite"
                                                        value={validation.values.priorite}
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                    >
                                                        <option value="basse">Basse</option>
                                                        <option value="normale">Normale</option>
                                                        <option value="haute">Haute</option>
                                                        <option value="urgente">Urgente</option>
                                                    </Input>
                                                </div>
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col md={12}>
                                                <div className="mb-3">
                                                    <Label htmlFor="description">Description détaillée</Label>
                                                    <Input
                                                        type="textarea"
                                                        className="form-control"
                                                        id="description"
                                                        name="description"
                                                        rows="6"
                                                        placeholder="Décrivez votre demande en détail..."
                                                        value={validation.values.description}
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        invalid={validation.touched.description && validation.errors.description}
                                                    />
                                                    {validation.touched.description && validation.errors.description && (
                                                        <FormFeedback>{validation.errors.description}</FormFeedback>
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>

                                        <div className="d-flex justify-content-end gap-2">
                                            <Button
                                                type="button"
                                                color="secondary"
                                                onClick={() => navigate('/my-ticket-requests')}
                                            >
                                                Annuler
                                            </Button>
                                            <Button type="submit" color="primary" disabled={loading}>
                                                {loading ? (
                                                    <>
                                                        <i className="bx bx-hourglass bx-spin me-2"></i>
                                                        Envoi en cours...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bx bx-send me-2"></i>
                                                        Envoyer la demande
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col lg={4}>
                            <Card>
                                <CardBody>
                                    <h4 className="card-title mb-3">💡 Conseils</h4>
                                    <ul className="text-muted">
                                        <li className="mb-2">Soyez précis dans votre titre</li>
                                        <li className="mb-2">Décrivez le problème ou la demande en détail</li>
                                        <li className="mb-2">Indiquez les étapes pour reproduire si c'est un bug</li>
                                        <li className="mb-2">Choisissez la bonne priorité</li>
                                    </ul>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
            <AlertModal
                isOpen={alertConfig.isOpen}
                toggle={toggleAlert}
                type={alertConfig.type}
                title={alertConfig.title}
                message={alertConfig.message}
            />
        </React.Fragment>
    );
};

export default CreateTicketRequest;
